import { FC, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Dropzone from 'react-dropzone';
import { useUploadProductImageMutation } from '@services';
import { JimboResponse } from '@models';
import { showToast } from '@store/slices/notification';
import { useAppDispatch } from '@hooks/redux-custom';
import { styled } from '@mui/material/styles';

type Props = {
  productId: number;
  displayModel: string;
};

export const ProductImageUploader: FC<Props> = ({ productId, displayModel }) => {
  const [uploadProductImage, { isLoading: uploadProductImageIsLoading }] = useUploadProductImageMutation();
  const dispatch = useAppDispatch();

  const defaultImageUploaderClassName = '';
  const [uploadBoxClassName, setUploadBoxClassName] = useState(defaultImageUploaderClassName);

  const handleImgOnDrop = (acceptedFiles: File[]) => {
    setUploadBoxClassName(defaultImageUploaderClassName);

    const formData = new FormData();

    formData.append('productImage', acceptedFiles[0]);

    uploadProductImage({ productId, formData })
      .unwrap()
      .then(() => {
        dispatch(showToast({ type: 'success', message: 'Successfully uploaded new product image!' }));
      })
      .catch((error: { data: JimboResponse }) => {
        dispatch(showToast({ type: 'error', message: 'Failed to upload product image!' }));
      });
  };

  const handleOnDragEnter = () => {
    setUploadBoxClassName('onDragEnter');
  };

  const handleOnDragLeave = () => {
    setUploadBoxClassName(defaultImageUploaderClassName);
  };

  const UploadSection = styled('section')`
    display: flex;
    justify-content: center;
    align-items: center;
  `;

  return (
    <Box
      sx={{
        transition: 'all 0.3s',
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '3px dashed #dedede',
        borderRadius: '6px',
        color: '#b4b4b4',
        cursor: 'pointer',
        '&:hover, &.onDragEnter': {
          borderColor: '#8d8d8d',
          color: '#000',
        },
      }}
      className={uploadBoxClassName}
    >
      <Dropzone
        onDrop={handleImgOnDrop}
        onDragEnter={handleOnDragEnter}
        onDragLeave={handleOnDragLeave}
        accept="image/*"
        multiple={false}
        maxFiles={1}
        minSize={1024}
        maxSize={3072000}
        noKeyboard={true}
      >
        {({ getRootProps, getInputProps }) => (
          <UploadSection {...getRootProps()}>
            <input {...getInputProps()} />
            {displayModel === 'large' && uploadProductImageIsLoading && <CircularProgress sx={{ position: 'absolute' }} />}
            {displayModel === 'large' ? (
              <Typography sx={{ padding: 2, minWidth: '730px', height: '150px', textAlign: 'center' }} marginBottom={1}>
                Drag and drop image here, or click to select the image
              </Typography>
            ) : uploadProductImageIsLoading ? (
              <CircularProgress />
            ) : (
              <Box sx={{ backgroundColor: '#ececec', width: '100%', height: '100%', cursor: 'pointer' }}>
                <AddIcon sx={{ color: '#888', width: '97px', height: '97px' }} />
              </Box>
            )}
          </UploadSection>
        )}
      </Dropzone>
    </Box>
  );
};
